// MethodScript Formatter - C# WASM Bridge
// This calls the C# MethodScriptFormatter compiled to WASM
//
// The formatter is implemented in C# at:
//   antlr/lsp/MethodScriptLSP.Antlr/Analysis/MethodScriptFormatter.cs
//
// Both editors use the same C# code:
//   - Blazor editor: calls directly via C# (antlr/blazor-editor/Services/AntlrParserService.cs)
//   - WASM editor: calls via WASM through this bridge

import { logger } from 'shared-monaco-methodscript';

interface WindowWithFormatter extends Window {
    MethodScriptFormatterService: typeof MethodScriptFormatterService;
}

class MethodScriptFormatterService {
    private wasmReady = false;
    private initPromise: Promise<void>;

    constructor() {
        this.initPromise = this.initialize();
    }

    private async initialize(): Promise<void> {
        const win = window as unknown as WindowWithFormatter;
        // Wait for the WASM runtime to be ready
        // The WASM module should export window.MethodScriptFormatterWASM
        if (typeof win.MethodScriptFormatterWASM !== 'undefined') {
            this.wasmReady = true;
            logger.info('MethodScriptFormatterService: C# WASM formatter loaded');
        } else {
            logger.warn('MethodScriptFormatterService: WASM formatter not loaded');
            logger.warn('Expected window.MethodScriptFormatterWASM to be defined');
            logger.warn('The C# formatter needs to be compiled to WASM');
        }
    }

    async formatDocument(text: string): Promise<string> {
        await this.initPromise;
        const win = window as unknown as WindowWithFormatter;

        // Re-check WASM readiness on each call to handle async loading
        if (!this.wasmReady && typeof win.MethodScriptFormatterWASM !== 'undefined') {
            this.wasmReady = true;
            logger.info('MethodScriptFormatterService: C# WASM formatter loaded (lazy)');
        }

        if (!this.wasmReady) {
            logger.error('WASM formatter not available');
            logger.error('C# MethodScriptFormatter needs to be compiled to WASM');
            logger.error('Source: antlr/lsp/MethodScriptLSP.Antlr/Analysis/MethodScriptFormatter.cs');
            return text; // Return unchanged
        }

        try {
            // Call the C# formatter through WASM
            return await win.MethodScriptFormatterWASM.applyFormatting(text);
        } catch (error) {
            logger.error('Error calling WASM formatter:', error);
            return text;
        }
    }
}

// Export for use in editor.ts
(window as unknown as WindowWithFormatter).MethodScriptFormatterService = MethodScriptFormatterService;

export { MethodScriptFormatterService };
